/* eslint-disable no-console */
import { Inject, Injectable } from '@angular/core';
import { AJSRootScope, CommonsSessionStorageService } from 'ib-web-commons/utils';
import { IbCheckinApisHttpTransformerService } from './ib-checkin-apis-http-transformer.service';
import { AJSPassengerAdditionalInfoService, AJSPassengerAdditionalInfoServiceType } from '@src/app/shared/services/ib-apis/ib-passenger-additional-info-service-bridge.service';
import { AJSCallApisFormService, AJSCallApisFormServiceType } from '@src/app/shared/services/ib-apis/ib-call-apis-form-service-bridge.service';
import { AJSModalService, AJSModalServiceType } from '@src/app/shared/services/ib-modal/ib-modal-service-bridge.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { CheckinAppConstants } from '@src/components/routing/ib-check-in-constants';
import { SecurityResponse } from '@src/app/core/models/ib-checkin-apis/services/security-model';
import { AdditonalInfoBody } from '@src/app/core/models/ib-checkin-apis/services/additional-info-model';
import { ApisPassengerState, ApisStatesCountries, errorApisTypesESTA, errorApisTypes } from '@src/app/core/constants/ib-checkin-apis-constants';
import { ApisPassenger, ErrorEstaConstraints, FormDataChecksAllPassengers } from '@src/app/core/models/ib-checkin-apis/ib-checkin-apis.model';
import { AJSGetCountriesListService, AJSGetCountriesListServiceType } from '@src/app/shared/services/ib-apis/ib-get-countries-list-service-bridge.service';
import { AJSGetPhoneCodesService, AJSGetPhoneCodesServiceType } from '@src/app/shared/services/ib-apis/ib-get-phone-codes-service-bridge.service';
import { AJSGetStatesListService, AJSGetStatesListServiceType } from '@src/app/shared/services/ib-apis/ib-get-states-list-service-bridge.service';
import { AJSGetCKICachedInformationServiceToken, AJSGetCKICachedInformationServiceType } from '@src/app/shared/services/ib-apis/ib-get-cached-information-service-bridge.service';
import { ApisErrorType, BookingInfoPassenger, FormDataApis } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/ApisForm.model';
import { CheckinRedirectionService } from '@src/app/shared/services/ib-utils/ib-checkin-redirection.service';
import { filter } from 'rxjs/operators';
import { AJSNewslettersService, AJSNewslettersServiceType } from '@src/app/shared/services/ib-apis/ib-newsletters-service-bridge.service';

@Injectable({ providedIn: 'root' })
export class IbCheckinApisService {
    private checksAllPassengersSubject: BehaviorSubject<FormDataChecksAllPassengers>;
    public readonly checkAllPassengers$: Observable<FormDataChecksAllPassengers>;

    constructor (
        @Inject(AJSRootScope) private $rootScope: CommonsRootScope,
        @Inject(AJSModalService) private ModalService: AJSModalServiceType,
        @Inject(AJSPassengerAdditionalInfoService) private PassengerAdditionalInfoService: AJSPassengerAdditionalInfoServiceType,
        @Inject(AJSCallApisFormService) private CallApisFormService: AJSCallApisFormServiceType,
        @Inject(AJSGetCountriesListService) private GetCountriesListService: AJSGetCountriesListServiceType,
        @Inject(AJSGetPhoneCodesService) private GetPhoneCodesService: AJSGetPhoneCodesServiceType,
        @Inject(AJSGetStatesListService) private GetStatesListService: AJSGetStatesListServiceType,
        @Inject(AJSGetCKICachedInformationServiceToken) private GetCKICachedInformationService: AJSGetCKICachedInformationServiceType,
        @Inject(AJSNewslettersService) private NewslettersService: AJSNewslettersServiceType,
        private CommonsSessionStorageService: CommonsSessionStorageService,
        private ApisHttpTransformerService: IbCheckinApisHttpTransformerService,
        private CheckinRedirectionService: CheckinRedirectionService
    ) {
        this.checksAllPassengersSubject = new BehaviorSubject<FormDataChecksAllPassengers>({});
        this.checkAllPassengers$ = this.checksAllPassengersSubject.asObservable().pipe(filter((data) => data !== null && data !== undefined && Object.keys(data).length > 0));
    }

    setCheckAllPassengersData (data: FormDataChecksAllPassengers) {
        this.checksAllPassengersSubject.next(data);
    }

    get getCheckAllPassengersData () {
        return this.checksAllPassengersSubject.asObservable().pipe(filter((data) => data !== null && data !== undefined && Object.keys(data).length > 0));
    }

    resetCheckAllPassengersData () {
        this.checksAllPassengersSubject.next(null);
    }

    async getApisInfo (): Promise<ApisPassenger[]> {
        const apisPassengersIds = this.CommonsSessionStorageService.get(CheckinAppConstants.STORAGE_KEYS.PASSENGERS_APIS);
        try {
            this.CommonsSessionStorageService.remove('countriesList');
            this.CommonsSessionStorageService.remove('phoneCodeList');
            const [countries, prefixList, states] = await Promise.all([
                this.GetCountriesListService.getCountriesList(null, null, true),
                this.GetPhoneCodesService.getPhoneCodesList(),
                this.GetStatesListService.getStatesList(ApisStatesCountries.US)
            ]);
            this.CommonsSessionStorageService.set(CheckinAppConstants.STORAGE_KEYS.APIS_INFO, {
                countries: countries,
                prefixList: prefixList,
                states: states
            });
            const securityResponse: SecurityResponse = await this.CallApisFormService.getApisInfo(apisPassengersIds);
            const additionalInfoResponse: AdditonalInfoBody = await this.PassengerAdditionalInfoService.getAdditionalInfo(apisPassengersIds);
            const apisPassengers: ApisPassenger[] = securityResponse.passengersSecurityInformation.map((securityPassenger) => ({
                ...securityPassenger,
                additionalInfo: additionalInfoResponse.find((additionalInfoPassenger) => securityPassenger.id.toString() === additionalInfoPassenger.id.toString()),
                formStatus: ApisPassengerState.PENDING,
                passengerBookingInfo: this.getPassengerBookingInfo(securityPassenger.id.toString())
            }));
            this.ModalService.isNotWaiting();

            return apisPassengers;
        } catch (error) {
            this.handleUpdateApisError(error);
        }
    }

    checkCasesConstraints (cachedBookingInfo, id) {
        const passengers = cachedBookingInfo?.passengers;
        const infoPassenger = this.findPassengerConstraints(passengers, id);
        const errorEstaConstraints: ErrorEstaConstraints = this.initializeErrorConstraints();
        let existEstaConstraints = this.checkConstraints(infoPassenger, errorEstaConstraints);

        if (existEstaConstraints) this.throwEstaConstraints(errorEstaConstraints);
    }

    private findPassengerConstraints (passengers, id) {
        return passengers?.find((passenger) => {
            return passenger.id == id && passenger.status?.code === CheckinAppConstants.GENERAL_PASSENGERS_STATUS.NOT_ALLOWED;
        });
    }

    private initializeErrorConstraints (): ErrorEstaConstraints {
        return {
            isEstaConstraints: false,
            isEesConstraints: false,
            isEtiasConstraints: false,
            isSeeAgent: false
        };
    }

    private checkConstraints (infoPassenger, errorEstaConstraints: ErrorEstaConstraints): boolean {
        let existEstaConstraints = false;

        if (infoPassenger?.status?.reason?.code.includes(CheckinAppConstants.GENERAL_PASSENGERS_STATUS.ESTA_CONSTRAINTS)) {
            errorEstaConstraints.isEstaConstraints = true;
            existEstaConstraints = true;
        }

        if (infoPassenger?.status?.reason?.code.includes(CheckinAppConstants.GENERAL_PASSENGERS_STATUS.EES_CONSTRAINTS)) {
            errorEstaConstraints.isEesConstraints = true;
            errorEstaConstraints.message = infoPassenger?.status?.reason?.message;
            existEstaConstraints = true;
        }

        if (infoPassenger?.status?.reason?.code.includes(CheckinAppConstants.GENERAL_PASSENGERS_STATUS.ETIAS_CONSTRAINTS)) {
            errorEstaConstraints.isEtiasConstraints = true;
            errorEstaConstraints.message = infoPassenger?.status?.reason?.message;
            existEstaConstraints = true;
        }

        return existEstaConstraints;
    }

    private throwEstaConstraints ({ isEstaConstraints, isEesConstraints, isEtiasConstraints, message = '', isSeeAgent }: ErrorEstaConstraints): ErrorEstaConstraints {
        const errorEstaConstraints: ErrorEstaConstraints = {
            isEstaConstraints: isEstaConstraints,
            isEesConstraints: isEesConstraints,
            isEtiasConstraints: isEtiasConstraints,
            message: message,
            isSeeAgent: isSeeAgent
        };
        throw errorEstaConstraints;
    }

    private throwError (error: any) {
        this.ModalService.showGenericError({ message: 'REST_SERVICE_ERROR_GENERAL', needsTranslate: true }, true);
        throw error;
    }

    private hasDataErrors (error: any): boolean {
        return Array.isArray(error?.data) && error.data.length > 0 && Array.isArray(error.data[0]?.errors) && error.data[0].errors.length > 0;
    }

    private isErrorTimatic (dataErrors: any[]): boolean {
        return dataErrors.length === 1 && errorApisTypes.includes(dataErrors[0]?.code);
    }

    private isErrorEsta (dataErrors: any[]): boolean {
        const isSingleError = dataErrors.length === 1 && errorApisTypesESTA.includes(dataErrors[0]?.code);
        const isMultiErrors = dataErrors.length > 1;
        return isSingleError || isMultiErrors;
    }

    private controlErrors (error: any) {
        this.checkConstraintErrors(error);
        this.checkDataErrors(error);
        this.throwError(error);
    }

    private checkConstraintErrors (error: any) {
        const errorEstaConstraints: ErrorEstaConstraints = {
            isEstaConstraints: error?.isEstaConstraints || false,
            isEesConstraints: error?.isEesConstraints || false,
            isEtiasConstraints: error?.isEtiasConstraints || false,
            isSeeAgent: error?.internalErrorCode === 'TRM_APIM_9038' || false
        };

        const existEstaConstraints =
            errorEstaConstraints.isEstaConstraints || errorEstaConstraints.isEesConstraints || errorEstaConstraints.isEtiasConstraints || errorEstaConstraints.isSeeAgent;

        if (existEstaConstraints) this.throwEstaConstraints(errorEstaConstraints);
    }

    private checkDataErrors (error: any) {
        if (this.hasDataErrors(error)) {
            const dataErrors = error.data[0].errors;

            if (this.isErrorTimatic(dataErrors)) {
                throw dataErrors;
            }
            if (this.isErrorEsta(dataErrors)) {
                this.throwEstaConstraints({ isEesConstraints: true });
            }
        }
    }

    async updateApisInfo (formData: FormDataApis, id) {
        try {
            await this.updateAdditionalInfo(formData, id);
            await this.updateSecurityInfo(formData, id);
            const newBookingInfo = await this.getCachedInformation();

            this.updateModelForAllPassengers(formData);
            this.CommonsSessionStorageService.set(CheckinAppConstants.STORAGE_KEYS.BOOKING_INFO, newBookingInfo);
            this.checkCasesConstraints(newBookingInfo, id);
        } catch (error: any) {
            this.controlErrors(error);
        }
    }

    updateModelForAllPassengers ({ contactData, passengerAddress }: FormDataApis) {
        const dataChecksAllPassengers: FormDataChecksAllPassengers = {};
        if (contactData) {
            const { additionalInfo, emergencyInfo } = contactData;
            // update additionInfo for all passengers
            if (additionalInfo?.checkInfoAll) {
                dataChecksAllPassengers.additionalInfo = additionalInfo;
            }

            // update emergencyInfo for all passengers
            if (emergencyInfo?.checkEmergencyAll) {
                dataChecksAllPassengers.emergencyInfo = emergencyInfo;
            }
        }

        //TODO: update address at destination for all passengers
        if (passengerAddress?.checkAddressForAll) {
            dataChecksAllPassengers.passengerAddress = passengerAddress;
        }
        this.setCheckAllPassengersData(dataChecksAllPassengers);
    }

    handleUpdateApisError (error) {
        this.ModalService.setErrorMessage(error.message);
        this.ModalService.setErrorCode(error.internalErrorCode);
        this.ModalService.isError(error);
    }

    async updateAdditionalInfo (formData: FormDataApis, id) {
        const isUserLogged = this.CommonsSessionStorageService.get(CheckinAppConstants.STORAGE_KEYS.USER_LOGGED);
        await this.PassengerAdditionalInfoService.updateAdditionalInfo(this.ApisHttpTransformerService.generateAdditionalInfoRequest(formData, id));
        if (!isUserLogged) this.NewslettersService.putNewsletters(this.ApisHttpTransformerService.generateNewsLetterInfo(formData));
    }

    async updateSecurityInfo (formData: FormDataApis, id) {
        await this.CallApisFormService.updateApisInfo(this.ApisHttpTransformerService.generateSecurityRequest(formData, id));
    }

    async getCachedInformation () {
        return await this.GetCKICachedInformationService.getCachedInformation();
    }

    getPassengerBookingInfo (id: string) {
        const bookingInfo: any = this.CommonsSessionStorageService.get(CheckinAppConstants.STORAGE_KEYS.BOOKING_INFO);
        const passengerInfo: BookingInfoPassenger = { id, name: '', surname: '' };
        if (Array.isArray(bookingInfo?.passengers)) {
            const passengers = bookingInfo.passengers;
            const passenger = passengers.find((pass) => pass.id.toString() === id);
            passengerInfo.name = passenger.name ?? '';
            passengerInfo.surname = passenger.surname ?? '';
            if (passenger.infant?.name && passenger.infant?.surname) passengerInfo.infant = { name: passenger.infant.name, surname: passenger.infant.surname };
        }

        return passengerInfo;
    }

    redirectToCheckin (path: string) {
        this.CheckinRedirectionService.path(path);
    }
}
